/**
 * 最近のページコンポーネント
 *
 * ダッシュボードで最近保存したページを表示
 */

import { Link } from 'react-router-dom';

function RecentPages({ pages }) {
  // ページがない場合
  if (!pages || pages.length === 0) {
    return (
      <div className="card p-8 text-center">
        <div className="text-4xl mb-4">📚</div>
        <h3 className="font-bold text-gray-900 mb-2">
          まだ保存したページがありません
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Chrome拡張機能からページを保存してみましょう
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {pages.map((page) => (
        <PageCard key={page.pageId} page={page} />
      ))}
    </div>
  );
}

/**
 * ページカードコンポーネント
 */
function PageCard({ page }) {
  // 日付をフォーマット
  const formatDate = (timestamp) => {
    if (!timestamp) return '不明';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    // 1時間以内
    if (diff < 60 * 60 * 1000) {
      const minutes = Math.floor(diff / (60 * 1000));
      return `${minutes}分前`;
    }

    // 今日
    if (date.toDateString() === now.toDateString()) {
      return `今日 ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    }

    // 昨日
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return '昨日';
    }

    // それ以外
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // ドメインを抽出
  const getDomain = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  };

  return (
    <Link
      to={`/pages/${page.pageId}`}
      className="card p-4 flex items-start gap-4 hover:shadow-md transition-shadow"
    >
      {/* ファビコン / プレースホルダー */}
      <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden">
        {page.favicon ? (
          <img
            src={page.favicon}
            alt=""
            className="w-8 h-8 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          className={`text-xl ${page.favicon ? 'hidden' : 'flex'}`}
          style={{ display: page.favicon ? 'none' : 'flex' }}
        >
          📄
        </span>
      </div>

      {/* コンテンツ */}
      <div className="flex-1 min-w-0">
        {/* タイトル */}
        <h3 className="font-medium text-gray-900 truncate">
          {page.title || 'タイトルなし'}
        </h3>

        {/* 要約 */}
        <p className="text-sm text-gray-500 truncate mt-1">
          {page.summary || getDomain(page.url) || 'AI要約を生成中...'}
        </p>

        {/* タグ・日時 */}
        <div className="flex items-center gap-2 mt-2">
          {/* タグ */}
          {page.autoTags && page.autoTags.length > 0 && (
            <div className="flex gap-1">
              {page.autoTags.slice(0, 2).map((tag, index) => (
                <span
                  key={index}
                  className="tag text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* 日時 */}
          <span className="text-xs text-gray-400 ml-auto">
            {formatDate(page.createdAt)}
          </span>
        </div>
      </div>

      {/* 矢印 */}
      <div className="flex-shrink-0 text-gray-300">
        →
      </div>
    </Link>
  );
}

export default RecentPages;
